import { gql, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons/index.tsx';
import { iconPlus } from '@sorare/core/src/atoms/icons/system/iconPlus';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  ANY_SPORT_PLAY_BOARDS_ARENA_ARENAMODE_FIXTURE,
  ANY_SPORT_PLAY_BOARDS_ARENA_ARENAMODE_FIXTURE_COMPETITION_LINEUPS,
  ANY_SPORT_PLAY_BOARDS_PRO,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { isTypeFn } from '@sorare/core/src/lib/gql/index.ts';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { ArenaPickers } from '#src/components/pickers/ArenaPickers/index.tsx';
import { Mode } from '#src/components/pickers/ModePicker/index.tsx';
import { arenaModeToGroupType, modeToArenaMode } from '#src/lib/gameMode.ts';
import { getFixtureQueryVariables } from '#src/lib/so5.ts';

import type {
  ArenaLineupsStickyFooterQuery,
  ArenaLineupsStickyFooterQueryVariables,
} from './__generated__/index.graphql.ts';

const ARENA_LINEUPS_STICKY_FOOTER_QUERY = gql`
  query ArenaLineupsStickyFooterQuery(
    $type: So5State
    $slug: String
    $sport: Sport!
    $eventType: So5FixtureEvent = CLASSIC
    $groupType: So5LeaderboardGroupType
    $competition: String!
  ) {
    so5 {
      so5Fixture(
        type: $type
        slug: $slug
        sport: $sport
        eventType: $eventType
      ) {
        slug
        so5LeaderboardGroups(groupType: $groupType, includeClosed: true) {
          slug
          ... on So5LeaderboardGroup {
            slug
            ...ArenaPickers_so5LeaderboardGroup
          }
        }
      }
      so5Leaderboard(slug: $competition) {
        slug
        mainRarityType
        canCompose {
          value
        }
      }
    }
  }
  ${ArenaPickers.fragments.so5LeaderboardGroup}
` as TypedDocumentNode<
  ArenaLineupsStickyFooterQuery,
  ArenaLineupsStickyFooterQueryVariables
>;

type Props = {
  fixture: string;
  competition: string;
  to?: string;
};

const StickyWrapper = styled(Vertical)`
  position: fixed;
  bottom: 0;
  width: 100%;
  max-width: 100%;
  padding: var(--double-unit);
`;

const Content = styled(Vertical)`
  @media ${tabletAndAbove} {
    margin: 0 auto;
  }
`;

export const ArenaLineupsStickyFooter = ({
  fixture,
  competition,
  to,
}: Props) => {
  const navigate = useNavigate();
  const { arenaMode = 'cash' } = useParams();
  const { sport, generateSportContextPath } = useSportContext();

  const navigateToLeaderboard = (leaderboardSlug: string) => {
    navigate(
      generateSportContextPath(
        ANY_SPORT_PLAY_BOARDS_ARENA_ARENAMODE_FIXTURE_COMPETITION_LINEUPS,
        { params: { arenaMode, fixture, competition: leaderboardSlug } }
      )
    );
  };

  const navigateToMode = (mode: Mode) => {
    if (mode === Mode.PRO) {
      navigate(generateSportContextPath(ANY_SPORT_PLAY_BOARDS_PRO));
    } else {
      const targetArenaMode = modeToArenaMode(mode);
      navigate(
        generateSportContextPath(
          ANY_SPORT_PLAY_BOARDS_ARENA_ARENAMODE_FIXTURE,
          {
            params: { arenaMode: targetArenaMode, fixture },
          }
        )
      );
    }
  };

  const groupType = arenaModeToGroupType(arenaMode);

  const { data } = useQuery(ARENA_LINEUPS_STICKY_FOOTER_QUERY, {
    variables: {
      ...getFixtureQueryVariables(fixture),
      sport,
      groupType,
      competition,
    },
  });

  const competitionGroups = (
    data?.so5.so5Fixture?.so5LeaderboardGroups ?? []
  ).filter(isTypeFn('So5LeaderboardGroup'));

  const selectedRarity =
    data?.so5.so5Leaderboard?.mainRarityType ?? Rarity.limited;
  const canCompose = data?.so5.so5Leaderboard?.canCompose?.value ?? false;

  return (
    <StickyWrapper>
      <Content center gap={2}>
        <ArenaPickers
          selectedRarity={selectedRarity}
          onSelectRarity={(_, suggestedLeaderboard) => {
            if (suggestedLeaderboard)
              navigateToLeaderboard(suggestedLeaderboard);
          }}
          onSelectMode={navigateToMode}
          competitionGroups={competitionGroups}
          selectedLeaderboardSlug={competition}
          onSelectLeaderboard={navigateToLeaderboard}
        />
        {!!canCompose && (
          <Button
            size="medium"
            color="primary"
            to={to}
            fullWidth
            startIcon={<FontAwesomeIcon icon={iconPlus} />}
          >
            <FormattedMessage defaultMessage="Add Team" />
          </Button>
        )}
      </Content>
    </StickyWrapper>
  );
};
